import type { HTMLAttributes } from 'react'
import { useId } from 'react'

import { Copy } from './Copy.js'
import { cx } from './core/classes.js'

/** Compact middle-truncated display for hashes and addresses. */
export function Hash(props: Hash.Props) {
  const { className, copy = false, isEvm = true, min = 4, value, ...rest } = props
  const hash = (
    <TruncatedHash
      className={cx('select-none', copy ? 'min-w-0 flex-1' : cx('w-[12ch]', className))}
      isEvm={isEvm}
      min={min}
      value={value}
      {...(!copy ? rest : {})}
    />
  )

  if (!copy) return hash

  return (
    <span
      className={cx('inline-flex max-w-full select-none items-center gap-1', className)}
      {...rest}
    >
      {hash}
      <Copy size="xs" value={value} variant="plain" />
    </span>
  )
}

function TruncatedHash(props: TruncatedHash.Props) {
  const { className, isEvm = true, min = 4, style, title, value, ...rest } = props
  const id = useId()
  const prefix = isEvm ? '0x' : ''
  const containerName = `rgn-hash-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`
  const body = isEvm && value.startsWith(prefix) ? value.slice(prefix.length) : value

  if (body.length < 2) {
    return (
      <span
        className={cx('font-mono tracking-normal', className)}
        style={{
          ...style,
          fontFamily:
            'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace)',
          letterSpacing: 0,
        }}
        title={title ?? value}
        {...rest}
      >
        {prefix}…
      </span>
    )
  }

  const midpoint = 1 + Math.ceil((body.length - 1) / 2)
  const minChars = prefix.length + min * 2
  const start = body.slice(1, midpoint)
  const end = body.slice(midpoint, -1)

  return (
    <span
      className={cx(
        'inline-flex w-full font-mono tracking-normal [text-decoration:inherit]',
        className,
      )}
      style={{
        ...style,
        containerName,
        containerType: 'inline-size',
        fontFamily:
          'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace)',
        letterSpacing: 0,
        minWidth: `${minChars + 1}ch`,
      }}
      title={title ?? value}
      {...rest}
    >
      <style>{`
        .${containerName}-ellipsis { display: none }
        @container ${containerName} (max-width: ${value.length + 1}ch) {
          .${containerName}-ellipsis { display: flex }
          .${containerName}-part {
            overflow: hidden;
            width: calc((100cqw - ${prefix.length + 3}ch) / 2);
          }
        }
      `}</style>
      <span
        style={{
          display: 'inline-flex',
          maxWidth: `${value.length - 1}ch`,
          minWidth: `${minChars}ch`,
          whiteSpace: 'nowrap',
          width: 'round(down, calc(100% - 1ch), 2ch)',
        }}
      >
        {prefix}
        {body.at(0)}
        <span className={`${containerName}-part`}>{start}</span>
        <span className={`${containerName}-ellipsis`}>…</span>
        <span
          className={`${containerName}-part`}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          {end}
        </span>
      </span>
      {body.at(-1)}
    </span>
  )
}

declare namespace TruncatedHash {
  type Props = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
    isEvm?: boolean | undefined
    min?: number | undefined
    value: string
  }
}

export declare namespace Hash {
  /** Props for {@link Hash}. */
  type Props = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
    /** Show an inline copy button after the hash. @default false */
    copy?: boolean | undefined
    /** Whether to display with an EVM `0x` prefix. @default true */
    isEvm?: boolean | undefined
    /** Minimum number of body characters to keep visible on each side. @default 4 */
    min?: number | undefined
    /** Hash or address value to display. */
    value: string
  }
}
